import { useState } from 'react';
import { faceNotation, squareName, type MatchView, type PlayLinkAction } from '../../engine';
import { t } from '../../i18n';
import { itemName, itemText, pawnRef, type Names } from '../../app/describe';
import { Modal } from './Modal';

/**
 * Playing a Trick (LEDGER.MD §12.2): which pawn makes the link (the first link names it) and
 * what the Trick is played on. One button per legal way to play it.
 */
export function TrickDialog({
  v,
  names,
  cardId,
  actions,
  chainPawn,
  onPick,
  onClose,
}: {
  v: MatchView;
  names: Names;
  cardId: string;
  /** Legal PLAY_LINK actions for this card (all with a `trick` target). */
  actions: readonly PlayLinkAction[];
  /** The chain pawn, once the chain has begun. */
  chainPawn: string | null;
  onPick: (a: PlayLinkAction) => void;
  onClose: () => void;
}) {
  const trick = v.cards[cardId]?.trick;
  const pawns = [...new Set(actions.map((a) => a.pawnId ?? chainPawn ?? ''))].filter(Boolean);
  const [pawn, setPawn] = useState(chainPawn ?? pawns[0] ?? '');
  const mine = actions.filter((a) => (a.pawnId ?? chainPawn) === pawn);
  const label = (a: PlayLinkAction): string => {
    const target = 'trick' in a.target ? a.target.trick : {};
    if (target.face) return faceNotation(target.face);
    if (target.pawnId) {
      const p = v.pawns[target.pawnId];
      return p?.square
        ? `${pawnRef(v, target.pawnId, names)} · ${squareName(p.square)}`
        : names.pawn(target.pawnId);
    }
    return t('trick.play');
  };
  if (!trick) return null;
  return (
    <Modal
      title={itemName(`trick.${trick}`)}
      onClose={onClose}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          {t('action.cancel')}
        </button>
      }
    >
      <p>{itemText(`trick.${trick}`)}</p>
      <p className="dim small">{itemText('trick')}</p>
      {!chainPawn && pawns.length > 1 && (
        <label className="field">
          <span className="field-label">{t('trick.chainPawn')}</span>
          <select value={pawn} onChange={(e) => setPawn(e.target.value)}>
            {pawns.map((id) => {
              const p = v.pawns[id];
              return (
                <option key={id} value={id}>
                  {pawnRef(v, id, names)}
                  {p?.square ? ` · ${squareName(p.square)}` : ''}
                </option>
              );
            })}
          </select>
        </label>
      )}
      <div className="curio-options">
        {mine.map((a, i) => (
          <button
            key={i}
            type="button"
            className={`btn ${'trick' in a.target && a.target.trick.face ? `card-face face-${a.target.trick.face.color}` : ''}`}
            data-autofocus={i === 0 ? true : undefined}
            onClick={() => onPick(a)}
          >
            {label(a)}
          </button>
        ))}
      </div>
    </Modal>
  );
}
